/* global theme */

if (!customElements.get('gift-progress')) {
  class GiftProgress extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold);
      this.variantId = Number(this.dataset.variantId);
      this.moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      this.textProgress = this.dataset.textProgress || '';
      this.textReached = this.dataset.textReached || '';

      this.bar = this.querySelector('.js-gift-bar');
      this.target = this.querySelector('.js-gift-target');
      this.text = this.querySelector('.js-gift-text');
      this.item = this.querySelector('.js-gift-item');
      this.addBtn = this.querySelector('.js-gift-add');
      this.added = this.querySelector('.js-gift-added');

      this.busy = false;

      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartUpdateHandler = this.handleCartUpdate.bind(this);
      GiftProgress.cartEvents.forEach((evt) => document.addEventListener(evt, this.cartUpdateHandler));

      if (this.addBtn) {
        this.addClickHandler = this.handleAddClick.bind(this);
        this.addBtn.addEventListener('click', this.addClickHandler);
      }

      // The cart can have changed since this block was rendered (e.g. a page served
      // from cache), so verify the current state once on load.
      this.handleCartUpdate();
    }

    disconnectedCallback() {
      GiftProgress.cartEvents.forEach((evt) => document.removeEventListener(evt, this.cartUpdateHandler));

      if (this.addBtn && this.addClickHandler) {
        this.addBtn.removeEventListener('click', this.addClickHandler);
      }
    }

    /**
     * Events after which the cart contents may have changed.
     * @returns {Array}
     */
    static get cartEvents() {
      return ['on:cart:add', 'on:line-item:change', 'on:cart-drawer:after-open'];
    }

    /**
     * Placeholder inside data-text-progress that holds the outstanding amount.
     * @returns {string}
     */
    static get amountPlaceholder() {
      return '[amount]';
    }

    /**
     * Refreshes this block from the current cart.
     */
    async handleCartUpdate() {
      const cart = await this.getCart();
      if (cart) this.render(cart);
    }

    /**
     * Fetches the current cart.
     * @returns {?object}
     */
    async getCart() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) throw new Error(response.status);
        return await response.json();
      } catch (error) {
        console.log(error); // eslint-disable-line
        return null;
      }
    }

    /**
     * Returns the cart subtotal, excluding the gift itself.
     * @param {Array} items - Cart line items.
     * @returns {number}
     */
    getQualifyingSubtotal(items) {
      return items.reduce(
        (total, item) => (item.variant_id === this.variantId ? total : total + item.final_line_price),
        0
      );
    }

    /**
     * Updates the block to match the given cart.
     * @param {object} cart - Cart object.
     */
    render(cart) {
      const items = cart.items || [];
      const giftLine = items.find((item) => item.variant_id === this.variantId);
      const subtotal = this.getQualifyingSubtotal(items);

      if (items.length === 0) {
        this.hidden = true;
        return;
      }

      // The gift no longer qualifies, so take it back out of the cart.
      if (giftLine && subtotal < this.threshold) {
        this.removeGift(giftLine.key);
        return;
      }

      this.hidden = false;
      this.bar.style.setProperty('--progress', `${Math.min(100, (subtotal / this.threshold) * 100)}%`);

      if (subtotal >= this.threshold) {
        this.text.textContent = this.textReached;
        this.target.hidden = true;
        this.item.hidden = false;
        this.addBtn.hidden = Boolean(giftLine);
        this.added.hidden = !giftLine;
      } else {
        this.text.textContent = this.textProgress.replace(
          GiftProgress.amountPlaceholder,
          this.formatMoney(this.threshold - subtotal)
        );
        this.target.hidden = false;
        this.item.hidden = true;
      }
    }

    /**
     * Adds the gift to the cart, at the customer's request.
     */
    async handleAddClick() {
      if (this.busy) return;
      this.setBusy(true);

      try {
        // Guard against adding the gift twice (e.g. added in another tab).
        const currentCart = await this.getCart();
        if (currentCart && currentCart.items.some((item) => item.variant_id === this.variantId)) {
          this.render(currentCart);
          return;
        }

        this.fetchRequestOpts.body = JSON.stringify({
          items: [{ id: this.variantId, quantity: 1 }]
        });

        const response = await fetch(theme.routes.cartAdd, this.fetchRequestOpts);
        if (!response.ok) throw new Error(response.status);
      } catch (error) {
        console.log(error); // eslint-disable-line
      } finally {
        this.setBusy(false);
        await this.handleCartUpdate();
        GiftProgress.refreshCart();
      }
    }

    /**
     * Removes the gift from the cart.
     * @param {string} key - Line item key of the gift.
     */
    async removeGift(key) {
      if (this.busy) return;
      this.busy = true;

      try {
        this.fetchRequestOpts.body = JSON.stringify({ id: key, quantity: 0 });

        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        if (!response.ok) throw new Error(response.status);

        const cart = await response.json();
        this.busy = false;
        this.render(cart);
        GiftProgress.refreshCart();
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.busy = false;
      }
    }

    /**
     * Toggles the busy state of the add button.
     * @param {boolean} busy - Whether a cart request is in flight.
     */
    setBusy(busy) {
      this.busy = busy;

      if (this.addBtn) {
        this.addBtn.disabled = busy;
        this.addBtn.setAttribute('aria-busy', busy);
      }
    }

    /**
     * Asks the cart drawer to rerender its line items, totals and cart icon.
     */
    static refreshCart() {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    }

    /**
     * Formats an amount (in cents) using the shop money format.
     * @param {number} cents - Amount in cents.
     * @returns {string}
     */
    formatMoney(cents) {
      const [whole, decimal] = (Math.abs(cents) / 100).toFixed(2).split('.');
      const separate = (separator) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
        switch (name) {
          case 'amount':
            return `${separate(',')}.${decimal}`;
          case 'amount_no_decimals':
            return separate(',');
          case 'amount_with_comma_separator':
            return `${separate('.')},${decimal}`;
          case 'amount_no_decimals_with_comma_separator':
            return separate('.');
          case 'amount_with_space_separator':
            return `${separate(' ')},${decimal}`;
          case 'amount_no_decimals_with_space_separator':
            return separate(' ');
          case 'amount_with_apostrophe_separator':
            return `${separate("'")}.${decimal}`;
          default:
            return match;
        }
      });
    }
  }

  customElements.define('gift-progress', GiftProgress);
}